import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Phone, 
  PhoneOff, 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  Minus,
  User,
  Activity
} from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface AgentPresence {
  id: string;
  organizationId: string;
  userId: string;
  agentId?: string;
  status: 'available' | 'busy' | 'offline' | 'away' | 'dnd';
  statusMessage?: string;
  maxConcurrentCalls: number;
  currentActiveCalls: number;
  skillTags: string[];
  queueMemberships: string[];
  lastActivity: string;
  lastCallAt?: string;
  totalCallsToday: number;
  sessionId?: string;
  createdAt: string;
  updatedAt: string;
}

const statusConfig = {
  available: {
    label: 'Available',
    color: 'bg-green-500',
    icon: CheckCircle2,
    variant: 'default' as const
  },
  busy: {
    label: 'Busy',
    color: 'bg-red-500',
    icon: Phone,
    variant: 'destructive' as const
  },
  offline: {
    label: 'Offline',
    color: 'bg-gray-500',
    icon: PhoneOff,
    variant: 'secondary' as const
  },
  away: {
    label: 'Away',
    color: 'bg-yellow-500',
    icon: Clock,
    variant: 'secondary' as const
  },
  dnd: {
    label: 'Do Not Disturb',
    color: 'bg-red-600',
    icon: Minus,
    variant: 'destructive' as const
  }
};

export function AgentPresenceIndicator() {
  const [statusMessage, setStatusMessage] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get current agent presence (real-time updates via WebSocket in NotificationsContext)
  const { data: presence, isLoading } = useQuery<AgentPresence>({
    queryKey: ['/api/agent-presence'],
    staleTime: Infinity, // Data stays fresh via WebSocket updates
  });

  // Update agent status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (data: { status: string; statusMessage?: string }) => {
      return await apiRequest('PUT', '/api/agent-presence/status', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/agent-presence'] });
      toast({
        title: "Status Updated",
        description: "Your agent status has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update status",
        variant: "destructive",
      });
    },
  });

  // Update status message when presence changes
  useEffect(() => {
    if (presence?.statusMessage) {
      setStatusMessage(presence.statusMessage);
    }
  }, [presence?.statusMessage]);

  const handleStatusChange = (newStatus: string) => {
    updateStatusMutation.mutate({
      status: newStatus,
      statusMessage: statusMessage || undefined
    });
  };

  const handleStatusMessageUpdate = () => {
    if (presence) {
      updateStatusMutation.mutate({
        status: presence.status,
        statusMessage: statusMessage || undefined
      });
    }
  };

  if (isLoading) {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="p-6">
          <div className="flex items-center space-x-3">
            <div className="animate-pulse w-4 h-4 bg-surface-elevated rounded-full"></div>
            <div className="animate-pulse h-4 bg-surface-elevated rounded w-24"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!presence) {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="p-6">
          <div className="flex items-center space-x-3 text-gray-500">
            <User className="w-4 h-4" />
            <span className="text-sm">Agent presence not available</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const config = statusConfig[presence.status];
  const StatusIcon = config.icon;

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center space-x-2">
          <Activity className="w-5 h-5" />
          <span>Agent Status</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Status Display */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${config.color}`}></div>
            <StatusIcon className="w-4 h-4" />
            <Badge variant={config.variant}>{config.label}</Badge>
          </div>
          <div className="text-sm text-gray-500">
            {presence.currentActiveCalls}/{presence.maxConcurrentCalls} calls
          </div>
        </div>

        {/* Status Selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Change Status</label>
          <Select 
            value={presence.status} 
            onValueChange={handleStatusChange}
            disabled={updateStatusMutation.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(statusConfig).map(([status, config]) => (
                <SelectItem key={status} value={status}>
                  {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Status Message */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Status Message</label>
          <div className="flex space-x-2">
            <Input
              placeholder="Optional status message..."
              value={statusMessage}
              onChange={(e) => setStatusMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleStatusMessageUpdate();
                }
              }}
              disabled={updateStatusMutation.isPending}
            />
            <Button
              size="sm"
              onClick={handleStatusMessageUpdate}
              disabled={updateStatusMutation.isPending}
            >
              Update
            </Button>
          </div>
        </div>

        {/* Queue Memberships */}
        {presence.queueMemberships.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Active Queues</label>
            <div className="flex flex-wrap gap-1">
              {presence.queueMemberships.map((queue) => (
                <Badge key={queue} variant="outline" className="text-xs">
                  {queue}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Today's Activity */}
        <div className="text-xs text-gray-500 pt-2 border-t">
          <div className="flex justify-between">
            <span>Calls Today:</span>
            <span className="font-medium">{presence.totalCallsToday}</span>
          </div>
          {presence.lastCallAt && (
            <div className="flex justify-between mt-1">
              <span>Last Call:</span>
              <span className="font-medium">
                {new Date(presence.lastCallAt).toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Compact version for headers/sidebars
export function AgentPresenceCompact() {
  const { data: presence } = useQuery<AgentPresence>({
    queryKey: ['/api/agent-presence'],
    staleTime: Infinity, // Data stays fresh via WebSocket updates
  });

  if (!presence) {
    return (
      <div className="flex items-center space-x-2 text-gray-500">
        <div className="w-2 h-2 rounded-full bg-gray-400"></div>
        <span className="text-xs">Offline</span>
      </div>
    );
  }

  const config = statusConfig[presence.status];

  return (
    <div className="flex items-center space-x-2">
      <div className={`w-2 h-2 rounded-full ${config.color}`}></div>
      <span className="text-xs font-medium">{config.label}</span>
      {presence.currentActiveCalls > 0 && (
        <Badge variant="secondary" className="text-xs px-1 py-0">
          {presence.currentActiveCalls}
        </Badge>
      )}
    </div>
  );
}